/**
 * The WG-Vermieter page (`/operators`), built to the website draft's "3e"
 * screen. Presentation only — the signup flow stays in `pages/Operators.tsx`.
 *
 * The ROI calculator reproduces the draft's model exactly; its numbers were
 * checked against the rendered draft at all three tiers (see landlord-data).
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useOwnerSelfSignupEnabled } from "@/hooks/useOwnerSelfSignupEnabled";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  Check,
  Lock,
  ArrowRight,
  ChevronRight,
  Star,
} from "lucide-react";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { SmoothImage } from "@/components/ui/smooth-image";
import { CountUpStat } from "@/components/marketing/count-up-stat";
import { WhatsAppIcon } from "@/components/marketing/whatsapp-icon";
import {
  INK,
  INK_DEEP,
  MUTED,
  MUTED_SOFT,
  GREY,
  SAND,
  LINE,
  LINE_SOFT,
  LINE_STRONG,
  IMAGE_BG,
  FIELD,
  EASE,
} from "@/lib/marketing-theme";
import {
  LANDLORD_STATS,
  OWNER_HOW,
  PRODUCT_CARDS,
  PLANS,
  COMPARISON,
  ONBOARDING,
  SAFETY,
  PARTNERS,
  TRUST,
  OWNER_FAQ,
  LANDLORD_VOICES,
  CALC_DEFAULTS,
  CALC_HOURLY_RATE,
  runCalculator,
  type Bilingual,
} from "./landlord-data";

type Lang = { de: boolean };
const pick = (v: Bilingual, de: boolean) => (de ? v.de : v.en);

/** German number formatting throughout — the draft's own convention. */
const eur = (n: number, de: boolean) =>
  new Intl.NumberFormat(de ? "de-DE" : "en-GB", {
    maximumFractionDigits: 0,
  }).format(Math.round(n)) + " €";

const H2 =
  "font-display text-[2rem] font-semibold leading-[1.06] sm:text-[2.5rem]";
const H2_STYLE = { letterSpacing: "-0.04em", color: INK } as const;

function Reveal({
  children,
  delay = 0,
  className,
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.7, delay: delay * 0.08, ease: EASE }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* Hero                                                                */
/* ------------------------------------------------------------------ */

export function LandlordHero({
  de,
  onPackages,
  onContact,
}: Lang & { onPackages: () => void; onContact: () => void }) {
  return (
    <section className="bg-white">
      <div
        className="mx-auto max-w-[1328px] px-5 sm:px-8 lg:px-14"
        style={{ paddingTop: "calc(var(--hero-offset) + 3rem)" }}
      >
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="font-display text-[2.5rem] font-semibold leading-[1.03] sm:text-[3.25rem] lg:text-[3.625rem]"
          style={{ letterSpacing: "-0.042em", color: INK }}
        >
          {de ? "Wir verwalten Ihre WG." : "We manage your shared flat."}
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.12, ease: EASE }}
          className="mt-8 flex flex-col items-start justify-between gap-7 pb-12 lg:flex-row lg:items-center"
        >
          <p
            className="max-w-[540px] text-[17px] leading-[1.6]"
            style={{ color: MUTED }}
          >
            {de
              ? "Im Schnitt sparen coliviq Kunden "
              : "On average, coliviq customers save "}
            <strong style={{ color: INK }}>
              {de ? "270 Stunden" : "270 hours"}
            </strong>
            {de
              ? " Arbeit im Jahr durch weniger Leerstand und Verwaltungsaufwand."
              : " of work per year through less vacancy and less admin."}
          </p>
          <div className="flex flex-wrap items-center gap-5">
            <button
              type="button"
              onClick={onPackages}
              className="inline-flex h-[54px] items-center rounded-full px-8 text-[16px] font-semibold text-white transition-transform duration-300 hover:scale-[1.03]"
              style={{ background: INK }}
            >
              {de ? "Pakete ansehen" : "See the packages"}
            </button>
            <button
              type="button"
              onClick={onContact}
              className="group inline-flex items-center gap-2.5 text-[16px] font-semibold"
              style={{ color: INK }}
            >
              {de ? "Kontakt aufnehmen" : "Get in touch"}
              <ArrowRight
                className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5"
                strokeWidth={2.4}
              />
            </button>
          </div>
        </motion.div>
      </div>

      {/* title image with the stats card sitting on it */}
      <div className="px-5 pb-16 sm:px-8 lg:px-14">
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
          className="relative mx-auto max-w-[1328px]"
        >
          <div
            className="relative h-[280px] overflow-hidden rounded-[14px] sm:h-[420px] lg:h-[520px]"
            style={{ background: IMAGE_BG }}
          >
            <SmoothImage
              src="/assets/own-hero-green-v2.jpg"
              alt=""
              wrapperClassName="absolute inset-0 h-full w-full"
              className="h-full w-full object-cover"
            />
          </div>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0, y: 24 },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.7,
                  delay: 0.45,
                  ease: EASE,
                  staggerChildren: 0.12,
                  delayChildren: 0.6,
                },
              },
            }}
            className="relative -mt-10 ml-0 flex w-fit max-w-full flex-col gap-0 overflow-hidden rounded-[14px] bg-white shadow-[0_24px_48px_rgba(9,46,44,0.14)] sm:ml-8 sm:flex-row lg:ml-10"
          >
            {LANDLORD_STATS.map((s, i) => (
              <motion.div
                key={s.value}
                variants={{
                  hidden: { opacity: 0, y: 10 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.5, ease: EASE },
                  },
                }}
                className="flex flex-col gap-1 px-7 py-6 sm:px-8"
                style={{
                  borderLeft: i > 0 ? `1px solid ${LINE_SOFT}` : undefined,
                }}
              >
                <span
                  className="text-[26px] font-semibold leading-none"
                  style={{ letterSpacing: "-0.04em", color: INK }}
                >
                  <CountUpStat value={s.value} de={de} />
                </span>
                <span className="text-[14px]" style={{ color: MUTED }}>
                  {pick(s.label, de)}
                </span>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Benefits + product cards                                            */
/* ------------------------------------------------------------------ */

export function LandlordBenefits({ de }: Lang) {
  return (
    <section className="bg-white px-5 py-20 sm:px-8 lg:px-14">
      <div className="mx-auto grid max-w-[1328px] items-start gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
        <Reveal>
          <h2 className={H2} style={H2_STYLE}>
            <span style={{ color: GREY }}>
              {de ? "Weniger Leerstand." : "Less vacancy."}
              <br />
              {de ? "Weniger Aufwand." : "Less admin."}
              <br />
            </span>
            {de ? "Mehr Rendite." : "More return."}
          </h2>
        </Reveal>
        <div className="flex flex-col">
          {OWNER_HOW.map((row, i) => (
            <Reveal key={row.name.en} delay={i}>
              <div
                className="grid gap-2 border-t py-7 sm:grid-cols-[minmax(0,240px)_minmax(0,1fr)] sm:gap-10"
                style={{ borderColor: LINE_STRONG }}
              >
                <span
                  className="text-[17px] font-semibold"
                  style={{ letterSpacing: "-0.02em", color: INK }}
                >
                  {pick(row.name, de)}
                </span>
                <span
                  className="text-[15.5px] leading-[1.6]"
                  style={{ color: MUTED }}
                >
                  {pick(row.body, de)}
                </span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * A browser frame around the card's app screenshot, so the three cards read
 * as product screens. The screenshot is pinned to its top-left corner: that is
 * where each screen's navigation and heading sit, so whichever edge the frame
 * crops — the bottom in a wide frame, the right in a narrow one — it keeps the
 * part that says what the screen is.
 */
function ScreenshotFrame({
  image,
  label,
  dark,
}: {
  image: string;
  label: string;
  dark?: boolean;
}) {
  return (
    <span
      className="flex h-full w-full flex-col"
      style={{ background: dark ? INK_DEEP : FIELD }}
    >
      <span className="flex items-center gap-1.5 px-4 py-3">
        {[0, 1, 2].map((d) => (
          <span
            key={d}
            className="h-2 w-2 rounded-full"
            style={{
              background: dark ? "rgba(247,243,236,0.28)" : "#D8D2C6",
            }}
          />
        ))}
      </span>
      <span className="relative flex-1 overflow-hidden px-3 pb-3">
        <span
          className="relative block h-full w-full overflow-hidden rounded-[10px]"
          style={{ background: IMAGE_BG }}
        >
          <SmoothImage
            src={image}
            alt={label}
            wrapperClassName="absolute inset-0 h-full w-full"
            className="h-full w-full object-cover object-left-top transition-transform duration-[900ms] ease-out group-hover/card:scale-[1.04]"
          />
        </span>
      </span>
    </span>
  );
}

export function LandlordProduct({ de }: Lang) {
  const [big, ...rest] = PRODUCT_CARDS;
  return (
    <section className="bg-white px-5 pb-24 sm:px-8 lg:px-14">
      <div className="mx-auto flex max-w-[1328px] flex-col gap-10">
        <Reveal>
          <h2 className={H2} style={H2_STYLE}>
            {de ? "Sechs Mietverhältnisse. " : "Six tenancies. "}
            <span style={{ color: GREY }}>
              {de ? "Ein Aufwand." : "One job."}
            </span>
          </h2>
        </Reveal>

        <div className="grid gap-4 lg:grid-cols-2">
          <Reveal>
            <article
              className="group/card flex h-full flex-col overflow-hidden rounded-[14px] border bg-white"
              style={{ borderColor: LINE }}
            >
              <div className="relative flex-1" style={{ minHeight: 340 }}>
                <ScreenshotFrame
                  image={pick(big.image, de)}
                  label={pick(big.shot, de)}
                />
              </div>
              <div className="flex flex-col gap-1.5 p-7">
                <span
                  className="text-[17px] font-semibold"
                  style={{ color: INK }}
                >
                  {pick(big.name, de)}
                </span>
                <span className="text-[15px]" style={{ color: MUTED }}>
                  {pick(big.body, de)}
                </span>
              </div>
            </article>
          </Reveal>

          <div className="flex flex-col gap-4">
            {rest.map((card, i) => (
              <Reveal key={card.name.en} delay={i + 1}>
                {card.dark ? (
                  /* The draft sets this card on its side: a small browser
                     window beside the copy, not a frame stacked above it. */
                  <article
                    /* Sideways is a 1440px layout. Below `sm` it squeezed the
                       copy into a ~119px column beside the frame, so the card
                       stacks there and turns on its side once there is room. */
                    className="group/card flex h-full flex-col items-start gap-5 rounded-[14px] p-[22px] sm:flex-row sm:items-center sm:gap-[22px]"
                    style={{ background: INK }}
                  >
                    {/* Narrower in the lg–xl band, where the card shares the
                        row with the big card and a 240px frame would leave
                        the copy a sliver again. The height follows the width
                        so the screenshot keeps its landscape crop. */}
                    <span className="block aspect-[4/3] w-full max-w-[360px] flex-none overflow-hidden rounded-[12px] sm:w-[240px] lg:w-[180px] xl:w-[240px]">
                      <ScreenshotFrame
                        dark
                        image={pick(card.image, de)}
                        label={pick(card.shot, de)}
                      />
                    </span>
                    <span className="flex min-w-0 flex-col gap-1.5">
                      <span className="text-[17px] font-semibold text-white">
                        {pick(card.name, de)}
                      </span>
                      <span
                        className="text-[15px]"
                        style={{ color: "rgba(247,243,236,0.8)" }}
                      >
                        {pick(card.body, de)}
                      </span>
                    </span>
                  </article>
                ) : (
                  <article
                    className="group/card flex h-full flex-col overflow-hidden rounded-[14px] border bg-white"
                    style={{ borderColor: LINE }}
                  >
                    <div className="relative" style={{ height: 210 }}>
                      <ScreenshotFrame
                        image={pick(card.image, de)}
                        label={pick(card.shot, de)}
                      />
                    </div>
                    <div className="flex flex-col gap-1.5 p-7">
                      <span
                        className="text-[17px] font-semibold"
                        style={{ color: INK }}
                      >
                        {pick(card.name, de)}
                      </span>
                      <span className="text-[15px]" style={{ color: MUTED }}>
                        {pick(card.body, de)}
                      </span>
                    </div>
                  </article>
                )}
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* ROI calculator                                                      */
/* ------------------------------------------------------------------ */

function CalcSlider({
  value,
  min,
  max,
  step,
  onChange,
  label,
}: {
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (n: number) => void;
  label: string;
}) {
  return (
    <SliderPrimitive.Root
      value={[value]}
      min={min}
      max={max}
      step={step}
      onValueChange={([v]) => onChange(v)}
      aria-label={label}
      className="relative flex w-full touch-none select-none items-center py-2"
    >
      <SliderPrimitive.Track
        className="relative h-1.5 w-full grow rounded-full"
        style={{ background: "#E4E0D6" }}
      >
        <SliderPrimitive.Range
          className="absolute h-full rounded-full"
          style={{ background: INK }}
        />
      </SliderPrimitive.Track>
      <SliderPrimitive.Thumb
        className="block h-5 w-5 rounded-full outline-none transition-transform duration-200 hover:scale-110 focus-visible:scale-110"
        style={{ background: INK }}
      />
    </SliderPrimitive.Root>
  );
}

export function LandlordCalculator({ de }: Lang) {
  const [tier, setTier] = useState(1);
  const [input, setInput] = useState(CALC_DEFAULTS);
  /** Which line's explanation is open; one at a time, as in the draft. */
  const [info, setInfo] = useState<string | null>(null);
  const r = useMemo(() => runCalculator(tier, input), [tier, input]);

  const fields = [
    {
      key: "rooms" as const,
      label: de ? "Zimmer" : "Rooms",
      min: 2,
      max: 20,
      step: 1,
      unit: "",
      minLabel: "2",
      maxLabel: "20",
    },
    {
      key: "rent" as const,
      label: de ? "Miete pro Zimmer" : "Rent per room",
      min: 300,
      max: 1500,
      step: 10,
      unit: "€",
      minLabel: "300 €",
      maxLabel: "1.500 €",
    },
    {
      key: "moves" as const,
      label: de ? "Mieterwechsel pro Jahr" : "Tenant changes per year",
      min: 0,
      max: 12,
      step: 1,
      unit: "",
      minLabel: "0",
      maxLabel: "12",
    },
    {
      key: "days" as const,
      label: de ? "Leerstandstage je Wechsel" : "Vacant days per change",
      min: 0,
      max: 60,
      step: 1,
      unit: de ? "Tage" : "days",
      minLabel: de ? "0 Tage" : "0 days",
      maxLabel: de ? "60 Tage" : "60 days",
    },
  ];

  /**
   * Every line carries the arithmetic behind it. The draft hides each one
   * behind its own control rather than printing four paragraphs under a
   * result card: the figures are a model, and a landlord who wants to know
   * where 35 €/hour or the 5 % came from can ask for exactly that line.
   */
  const rows = [
    {
      key: "cost",
      label: de ? "coliviq-Kosten" : "coliviq cost",
      note: `${eur(r.unit, de)} × ${r.rooms} ${de ? "Zimmer" : "rooms"} × 12`,
      value: `${eur(r.costYear, de)} / ${de ? "Jahr" : "year"}`,
      info: de
        ? `Der Jahrespreis des gewählten Pakets (${eur(r.unit, de)} pro Zimmer und Monat), mal Ihrer Zimmerzahl, mal zwölf Monaten.`
        : `The annual price of the selected package (${eur(r.unit, de)} per room per month), times your number of rooms, times twelve.`,
    },
    {
      key: "vacancy",
      label: de ? "Vermiedener Leerstand" : "Vacancy avoided",
      note: de
        ? `${input.moves} Wechsel × ${input.days} Tage`
        : `${input.moves} changes × ${input.days} days`,
      value: `+ ${eur(r.vacancy, de)}`,
      info: de
        ? "Tagesmiete = Monatsmiete ÷ 30. Multipliziert mit den Leerstandstagen je Wechsel und der Zahl der Wechsel pro Jahr — die Tage, die durch Nachmietersuche vor dem Auszug wegfallen."
        : "Daily rent = monthly rent ÷ 30, multiplied by the vacant days per change and the number of changes per year — the days you save by finding the next tenant before the current one leaves.",
    },
    {
      key: "time",
      label: de ? "Gesparte Verwaltungszeit" : "Admin time saved",
      note: de
        ? `${r.hours} Std. pro Monat × ${CALC_HOURLY_RATE} €`
        : `${r.hours} hrs per month × €${CALC_HOURLY_RATE}`,
      value: `+ ${eur(r.time, de)}`,
      info: de
        ? `Erfahrungswert aus unserem eigenen Portfolio: ${r.hours} Stunden weniger Verwaltung pro Monat in diesem Paket, bewertet mit ${CALC_HOURLY_RATE} € Stundensatz.`
        : `From our own portfolio: ${r.hours} hours less admin per month on this package, valued at €${CALC_HOURLY_RATE} an hour.`,
    },
    ...(r.external > 0
      ? [
          {
            key: "ext",
            label: de
              ? "Kein externer Verwalter nötig"
              : "No external manager needed",
            note: de ? "5 % der Jahresmiete" : "5 % of annual rent",
            value: `+ ${eur(r.external, de)}`,
            info: de
              ? "Eine Sondereigentumsverwaltung kostet üblicherweise rund 5 % der Jahresmiete. In der Sorglos-Verwaltung ist diese Leistung enthalten — inklusive Handwerkerkoordination und Mieterbetreuung."
              : "Managing a single unit externally typically costs around 5 % of the annual rent. Fully managed includes it — contractor coordination and tenant support alongside.",
          },
        ]
      : []),
  ];

  return (
    <section
      className="px-5 py-24 sm:px-8 lg:px-14"
      style={{ background: SAND }}
      id="rechner"
    >
      <div className="mx-auto flex max-w-[1328px] flex-col gap-10">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <h2 className={H2} style={H2_STYLE}>
              {de ? "Rechnen Sie selbst." : "Do the maths yourself."}
            </h2>
            <span className="text-[15px]" style={{ color: MUTED_SOFT }}>
              {de
                ? "Tragen Sie Ihre Zahlen ein. Preise im Jahresplan."
                : "Enter your own numbers. Prices on the annual plan."}
            </span>
          </div>
        </Reveal>

        <Reveal>
          <div className="grid overflow-hidden rounded-[18px] shadow-[0_24px_60px_-40px_rgba(9,46,44,0.4)] lg:grid-cols-[1.15fr_0.85fr]">
            {/* inputs */}
            <div className="flex flex-col gap-7 bg-white p-8 sm:p-12">
              <span
                className="text-[24px] font-semibold"
                style={{ letterSpacing: "-0.03em", color: INK }}
              >
                {de ? "Ihre Angaben." : "Your numbers."}
              </span>

              <div className="flex flex-col gap-3">
                <span
                  className="text-[16px] font-semibold"
                  style={{ color: INK }}
                >
                  {de ? "Paket" : "Package"}
                </span>
                <span className="text-[14px]" style={{ color: MUTED }}>
                  {de
                    ? "Bestimmt Preis und gesparte Verwaltungszeit."
                    : "Sets the price and the admin time saved."}
                </span>
                <div
                  className="mt-1 inline-flex w-fit max-w-full flex-wrap gap-1 rounded-full p-1.5"
                  style={{ background: "#EFEAE1" }}
                >
                  {PLANS.map((p, i) => {
                    const on = tier === i;
                    return (
                      <button
                        key={p.name.en}
                        type="button"
                        onClick={() => setTier(i)}
                        aria-pressed={on}
                        className="relative h-[38px] rounded-full px-[18px] text-[14px] font-semibold transition-colors"
                        style={{ color: on ? INK : MUTED }}
                      >
                        {on && (
                          <motion.span
                            layoutId="calc-tier"
                            transition={{ duration: 0.3, ease: EASE }}
                            className="absolute inset-0 rounded-full bg-white shadow-[0_1px_4px_rgba(9,46,44,0.12)]"
                          />
                        )}
                        <span className="relative">{pick(p.name, de)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div
                className="grid gap-x-10 gap-y-7 border-t pt-7 sm:grid-cols-2"
                style={{ borderColor: LINE }}
              >
                {fields.map((f) => (
                  <label key={f.key} className="flex flex-col gap-2">
                    <span className="flex items-center justify-between gap-4">
                      <span
                        className="text-[15px] font-semibold"
                        style={{ color: INK }}
                      >
                        {f.label}
                      </span>
                      <span
                        className="inline-flex h-11 min-w-[92px] items-center justify-end gap-1.5 rounded-[10px] border px-3.5 text-[16px] font-semibold tabular-nums"
                        style={{ borderColor: LINE, color: INK }}
                      >
                        {input[f.key]}
                        {f.unit && (
                          <span
                            className="text-[14px] font-medium"
                            style={{ color: GREY }}
                          >
                            {f.unit}
                          </span>
                        )}
                      </span>
                    </span>
                    <CalcSlider
                      value={input[f.key]}
                      min={f.min}
                      max={f.max}
                      step={f.step}
                      label={f.label}
                      onChange={(v) =>
                        setInput((prev) => ({ ...prev, [f.key]: v }))
                      }
                    />
                    <span
                      className="flex justify-between text-[12.5px]"
                      style={{ color: GREY }}
                    >
                      <span>{f.minLabel}</span>
                      <span>{f.maxLabel}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* result */}
            <div
              className="flex flex-col p-8 sm:p-12"
              style={{ background: INK }}
            >
              <span
                className="pb-5 text-[24px] font-semibold text-white"
                style={{ letterSpacing: "-0.03em" }}
              >
                {de ? "Ihr Ergebnis." : "Your result."}
              </span>
              {rows.map((row) => {
                const isOpen = info === row.key;
                const infoId = `calc-info-${row.key}`;
                return (
                  <div
                    key={row.key}
                    className="flex flex-col gap-3 border-t py-[18px]"
                    style={{ borderColor: "rgba(255,255,255,0.14)" }}
                  >
                    <div className="flex items-baseline justify-between gap-6">
                      <span className="flex min-w-0 flex-col gap-0.5">
                        <span className="flex items-center gap-2">
                          <span
                            className="text-[15px]"
                            style={{ color: "rgba(247,243,236,0.78)" }}
                          >
                            {row.label}
                          </span>
                          <button
                            type="button"
                            onClick={() => setInfo(isOpen ? null : row.key)}
                            aria-expanded={isOpen}
                            aria-controls={infoId}
                            className="grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full text-[11px] font-semibold transition-colors"
                            style={{
                              background: isOpen
                                ? "rgba(255,255,255,0.26)"
                                : "rgba(255,255,255,0.12)",
                              color: "rgba(247,243,236,0.9)",
                            }}
                          >
                            <span className="sr-only">
                              {de
                                ? `Erklärung zu „${row.label}“ ${isOpen ? "ausblenden" : "anzeigen"}`
                                : `${isOpen ? "Hide" : "Show"} how “${row.label}” is calculated`}
                            </span>
                            <span aria-hidden>i</span>
                          </button>
                        </span>
                        <span
                          className="truncate text-[12.5px] tabular-nums"
                          style={{ color: "rgba(247,243,236,0.5)" }}
                        >
                          {row.note}
                        </span>
                      </span>
                      <motion.span
                        key={row.value}
                        initial={{ opacity: 0.4 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.25 }}
                        className="whitespace-nowrap text-[20px] font-semibold text-white tabular-nums"
                        style={{ letterSpacing: "-0.03em" }}
                      >
                        {row.value}
                      </motion.span>
                    </div>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.p
                          id={infoId}
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.25, ease: EASE }}
                          className="overflow-hidden rounded-[10px] px-[14px] py-3 text-[13.5px] leading-[1.5]"
                          style={{
                            background: "rgba(255,255,255,0.08)",
                            color: "rgba(247,243,236,0.86)",
                          }}
                        >
                          {row.info}
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
              {/*
               * The model can come out negative — a large, stable portfolio
               * on a cheap package saves less admin than it costs. Calling
               * that "Sie sparen −2.580 €" would be worse than showing
               * nothing, so the label follows the sign.
               */}
              <div
                className="mt-auto flex flex-col gap-3 border-t pt-7"
                style={{ borderColor: "rgba(255,255,255,0.6)" }}
              >
                <div className="flex flex-wrap items-end justify-between gap-4">
                  <span className="flex flex-col gap-1">
                    <span className="flex items-center gap-2">
                      <span className="text-[15px] font-semibold text-white">
                        {r.net >= 0
                          ? de
                            ? "Sie sparen"
                            : "You save"
                          : de
                            ? "Draufzahlung"
                            : "Net cost"}
                      </span>
                      <button
                        type="button"
                        onClick={() => setInfo(info === "net" ? null : "net")}
                        aria-expanded={info === "net"}
                        aria-controls="calc-info-net"
                        className="grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full text-[11px] font-semibold transition-colors"
                        style={{
                          background:
                            info === "net"
                              ? "rgba(255,255,255,0.26)"
                              : "rgba(255,255,255,0.12)",
                          color: "rgba(247,243,236,0.9)",
                        }}
                      >
                        <span className="sr-only">
                          {de
                            ? `Erklärung zum Ergebnis ${info === "net" ? "ausblenden" : "anzeigen"}`
                            : `${info === "net" ? "Hide" : "Show"} how the result is calculated`}
                        </span>
                        <span aria-hidden>i</span>
                      </button>
                    </span>
                    <span
                      className="text-[13.5px]"
                      style={{ color: "rgba(247,243,236,0.6)" }}
                    >
                      {r.net >= 0
                        ? `≈ ${eur(r.net / 12, de)} ${de ? "pro Monat" : "per month"}`
                        : de
                          ? "In diesem Szenario rechnet es sich nicht"
                          : "On these inputs it does not pay for itself"}
                    </span>
                  </span>
                  <motion.span
                    key={r.net}
                    initial={{ opacity: 0.4, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, ease: EASE }}
                    className="text-[34px] font-semibold text-white tabular-nums sm:text-[40px]"
                    style={{ letterSpacing: "-0.04em" }}
                  >
                    {r.net >= 0 ? "" : "− "}
                    {eur(Math.abs(r.net), de)} / {de ? "Jahr" : "year"}
                  </motion.span>
                </div>
                <AnimatePresence initial={false}>
                  {info === "net" && (
                    <motion.p
                      id="calc-info-net"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.25, ease: EASE }}
                      className="overflow-hidden rounded-[10px] px-[14px] py-3 text-[13.5px] leading-[1.5]"
                      style={{
                        background: "rgba(255,255,255,0.08)",
                        color: "rgba(247,243,236,0.86)",
                      }}
                    >
                      {/* Sorglos-Verwaltung adds the external-manager saving
                          to the sum, and it is shown as its own line above.
                          Leaving it out of the sentence made the stated
                          formula disagree with the figure beside it by the
                          whole of that line — 2.484 € on the defaults. */}
                      {de
                        ? r.external > 0
                          ? "Vermiedener Leerstand plus Wert der gesparten Verwaltungszeit plus die eingesparte Verwaltergebühr, minus die coliviq-Kosten — alles auf ein Jahr gerechnet."
                          : "Vermiedener Leerstand plus Wert der gesparten Verwaltungszeit, minus die coliviq-Kosten — alles auf ein Jahr gerechnet."
                        : r.external > 0
                          ? "Vacancy avoided plus the value of the admin time saved plus the external manager's fee you no longer pay, less what coliviq costs — all over one year."
                          : "Vacancy avoided plus the value of the admin time saved, less what coliviq costs — all over one year."}
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>
              <p
                className="mt-5 text-[13px] leading-[1.5]"
                style={{ color: "rgba(247,243,236,0.6)" }}
              >
                {de
                  ? "Modellrechnung mit Ihren Eingaben: Tagesmiete = Monatsmiete ÷ 30, Zeitwert = gesparte Stunden × 35 €. Keine garantierte Ersparnis."
                  : "A model based on your inputs: daily rent = monthly rent ÷ 30, time valued at saved hours × €35. Not a guaranteed saving."}
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Pricing + comparison                                                */
/* ------------------------------------------------------------------ */

export function LandlordPricing({
  de,
  onStart,
}: Lang & { onStart: () => void }) {
  const [yearly, setYearly] = useState(true);
  const [compareOpen, setCompareOpen] = useState(false);

  // Where "Jetzt starten" goes (Neel, 2026-10-05): all three packages lead to
  // the free owner account rather than the demo-call pop-up. Picking a package
  // and then being asked to book a call is a step backwards — the account is
  // free, the package is chosen inside it, and the contract follows later.
  //
  // The pop-up stays as the fallback, not as a second option: while owner
  // self-signup is off (or the gate has not answered yet) /owners/signup says
  // "Noch nicht verfügbar", and a dead end is worse than the call that used to
  // be here. `null` therefore behaves like `false` — the button never changes
  // under a cursor that is already on it.
  const signupOpen = useOwnerSelfSignupEnabled() === true;

  return (
    <section
      className="px-5 py-24 sm:px-8 lg:px-14"
      style={{ background: SAND }}
      id="pakete"
    >
      <div className="mx-auto flex max-w-[1328px] flex-col gap-10">
        <Reveal>
          <div className="flex flex-wrap items-center gap-6">
            <h2 className={H2} style={H2_STYLE}>
              {de
                ? "Wie viel soll coliviq Ihnen abnehmen?"
                : "How much should coliviq take off your hands?"}
            </h2>
            <div
              className="inline-flex gap-1 rounded-full p-1.5"
              style={{ background: "#EFEAE1" }}
            >
              {[
                { on: !yearly, label: de ? "Monatlich" : "Monthly" },
                {
                  on: yearly,
                  // "bis zu": the discount applies to Selbstverwaltung and
                  // Smartverwaltung (19->16, 29->25). Sorglos is 49 € on both
                  // plans, so a flat "15 % sparen" would promise that package
                  // a saving it does not give.
                  label: de
                    ? "Jährlich · bis zu 15 % sparen"
                    : "Yearly · save up to 15 %",
                },
              ].map((b, i) => (
                <button
                  key={b.label}
                  type="button"
                  onClick={() => setYearly(i === 1)}
                  aria-pressed={b.on}
                  className="relative h-[38px] rounded-full px-[18px] text-[14px] font-semibold"
                  style={{ color: b.on ? INK : MUTED }}
                >
                  {b.on && (
                    <motion.span
                      layoutId="billing-toggle"
                      transition={{ duration: 0.3, ease: EASE }}
                      className="absolute inset-0 rounded-full bg-white shadow-[0_1px_4px_rgba(9,46,44,0.12)]"
                    />
                  )}
                  <span className="relative">{b.label}</span>
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="grid gap-5 lg:grid-cols-3">
          {PLANS.map((plan, i) => {
            const dark = i === 2;
            const price = yearly ? plan.yearly : plan.monthly;
            return (
              <Reveal key={plan.name.en} delay={i}>
                <article
                  className="relative flex h-full flex-col gap-6 rounded-[18px] p-8 sm:p-9"
                  style={
                    dark
                      ? {
                          background: INK,
                          boxShadow: "0 30px 60px -30px rgba(9,46,44,0.5)",
                        }
                      : {
                          background: "#FFFFFF",
                          border: `1px solid ${LINE}`,
                        }
                  }
                >
                  {plan.badge && (
                    <span
                      className="absolute -top-4 right-6 whitespace-nowrap rounded-full bg-white px-4 py-2 text-[13px] font-semibold shadow-[0_8px_20px_rgba(9,46,44,0.16)]"
                      style={{ color: INK }}
                    >
                      {pick(plan.badge, de)}
                    </span>
                  )}
                  <span
                    className="text-[13px] font-bold uppercase tracking-[0.14em]"
                    style={{
                      color: dark ? "rgba(247,243,236,0.72)" : MUTED,
                    }}
                  >
                    {pick(plan.name, de)}
                  </span>
                  <span className="flex flex-col gap-1">
                    <motion.span
                      key={price}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, ease: EASE }}
                      className="text-[40px] font-semibold leading-none"
                      style={{
                        letterSpacing: "-0.04em",
                        color: dark ? "#FFFFFF" : INK,
                      }}
                    >
                      {price} €
                    </motion.span>
                    <span
                      className="text-[14px]"
                      style={{
                        color: dark ? "rgba(247,243,236,0.6)" : MUTED,
                      }}
                    >
                      {de ? "pro Zimmer / Monat" : "per room / month"}
                    </span>
                  </span>

                  {plan.inherit && (
                    <span
                      className="text-[14.5px] font-semibold"
                      style={{ color: dark ? "#FFFFFF" : INK }}
                    >
                      {pick(plan.inherit, de)}
                    </span>
                  )}
                  <ul className="flex flex-1 flex-col gap-3">
                    {plan.bullets.map((b) => (
                      <li key={b.en} className="flex gap-2.5">
                        <Check
                          className="mt-0.5 h-4 w-4 shrink-0"
                          style={{ color: dark ? "#FFFFFF" : INK }}
                          strokeWidth={2.6}
                        />
                        <span
                          className="text-[15px] leading-[1.5]"
                          style={{
                            color: dark ? "rgba(247,243,236,0.86)" : MUTED,
                          }}
                        >
                          {pick(b, de)}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {(() => {
                    const label = de ? "Jetzt starten" : "Get started";
                    const className =
                      "inline-flex h-[54px] w-full items-center justify-center rounded-full text-[16px] font-semibold transition-transform duration-300 hover:scale-[1.02]";
                    const style = dark
                      ? { background: "#FFFFFF", color: INK }
                      : { border: `1.5px solid ${INK}`, color: INK };
                    return signupOpen ? (
                      <Link
                        to="/owners/signup"
                        className={className}
                        style={style}
                      >
                        {label}
                      </Link>
                    ) : (
                      <button
                        type="button"
                        onClick={onStart}
                        className={className}
                        style={style}
                      >
                        {label}
                      </button>
                    );
                  })()}
                  <span
                    className="text-center text-[13px]"
                    style={{
                      color: dark ? "rgba(247,243,236,0.6)" : MUTED_SOFT,
                    }}
                  >
                    {pick(plan.foot, de)}
                    {!dark &&
                      (yearly
                        ? de
                          ? " · jährlich abgerechnet"
                          : " · billed yearly"
                        : de
                          ? " · monatlich abgerechnet"
                          : " · billed monthly")}
                  </span>
                </article>
              </Reveal>
            );
          })}
        </div>

        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => setCompareOpen((v) => !v)}
            aria-expanded={compareOpen}
            className="inline-flex h-[52px] items-center gap-2.5 rounded-full border bg-white px-7 text-[15px] font-semibold transition-transform duration-300 hover:-translate-y-0.5"
            style={{ borderColor: LINE, color: INK }}
          >
            {de ? "Alle Funktionen vergleichen" : "Compare all features"}
            <ChevronDown
              className={`h-4 w-4 transition-transform duration-300 ${
                compareOpen ? "rotate-180" : ""
              }`}
              strokeWidth={2.4}
            />
          </button>
        </div>

        <AnimatePresence initial={false}>
          {compareOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="overflow-hidden"
            >
              <ComparisonTable de={de} yearly={yearly} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}

function Mark({ on, dark }: { on: boolean; dark?: boolean }) {
  if (on)
    return (
      <Check
        className="mx-auto h-[19px] w-[19px]"
        style={{ color: dark ? "#FFFFFF" : INK }}
        strokeWidth={2.5}
      />
    );
  return (
    <Lock
      className="mx-auto h-[17px] w-[17px]"
      style={{ color: "#B9C2C0" }}
      strokeWidth={2}
    />
  );
}

function ComparisonTable({ de, yearly }: Lang & { yearly: boolean }) {
  const heads = [
    {
      name: PLANS[0].name,
      price: `${yearly ? 16 : 19} € / ${de ? "Zimmer" : "room"}`,
    },
    {
      name: PLANS[1].name,
      price: `${yearly ? 25 : 29} € / ${de ? "Zimmer" : "room"}`,
    },
    {
      name: PLANS[2].name,
      price: de ? "49 € · all-inclusive" : "€49 · all-inclusive",
    },
  ];
  const cols =
    "grid-cols-[minmax(0,1fr)_92px_92px_104px] sm:grid-cols-[minmax(0,1fr)_150px_150px_170px]";

  return (
    <div className="pt-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <span
          className="text-[22px] font-semibold"
          style={{ letterSpacing: "-0.03em", color: INK }}
        >
          {de ? "Vollständiger Vergleich" : "Full comparison"}
        </span>
        <span
          className="flex items-center gap-5 text-[14px]"
          style={{ color: MUTED }}
        >
          <span className="inline-flex items-center gap-1.5">
            <Check
              className="h-4 w-4"
              style={{ color: INK }}
              strokeWidth={2.5}
            />
            {de ? "enthalten" : "included"}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Lock
              className="h-3.5 w-3.5"
              style={{ color: "#B9C2C0" }}
              strokeWidth={2}
            />
            {de ? "nicht enthalten" : "not included"}
          </span>
        </span>
      </div>

      <div
        className="overflow-x-auto rounded-[18px] border bg-white"
        style={{ borderColor: LINE }}
      >
        <div className="min-w-[640px]">
          {/* header */}
          <div className={`grid ${cols} items-stretch`}>
            <span
              className="px-6 py-6 text-[12.5px] font-bold uppercase tracking-[0.14em]"
              style={{ color: MUTED }}
            >
              {de ? "Funktion" : "Feature"}
            </span>
            {heads.map((h, i) => (
              <span
                key={h.name.en}
                className="flex flex-col items-center justify-center gap-0.5 px-3 py-6 text-center"
                style={
                  i === 2
                    ? { background: INK, borderRadius: "14px 14px 0 0" }
                    : undefined
                }
              >
                <span
                  className="text-[14.5px] font-semibold"
                  style={{ color: i === 2 ? "#FFFFFF" : INK }}
                >
                  {pick(h.name, de)}
                </span>
                <span
                  className="text-[12.5px]"
                  style={{ color: i === 2 ? "rgba(247,243,236,0.72)" : MUTED }}
                >
                  {h.price}
                </span>
              </span>
            ))}
          </div>

          {COMPARISON.map((group) => (
            <div key={group.group.en}>
              <div className={`grid ${cols}`} style={{ background: SAND }}>
                <span
                  className="px-6 py-3.5 text-[12.5px] font-bold uppercase tracking-[0.14em]"
                  style={{ color: INK }}
                >
                  {pick(group.group, de)}
                </span>
                <span />
                <span />
                <span style={{ background: "rgba(9,46,44,0.04)" }} />
              </div>
              {group.rows.map(([name, note, a, b, c]) => (
                <div
                  key={name.en}
                  className={`grid ${cols} items-center border-t`}
                  style={{ borderColor: LINE_SOFT }}
                >
                  <span className="flex flex-col gap-0.5 px-6 py-4">
                    <span
                      className="text-[15px] font-semibold"
                      style={{ color: INK }}
                    >
                      {pick(name, de)}
                    </span>
                    <span className="text-[13.5px]" style={{ color: MUTED }}>
                      {pick(note, de)}
                    </span>
                  </span>
                  <span className="px-3 py-4">
                    <Mark on={a === 1} />
                  </span>
                  <span className="px-3 py-4">
                    <Mark on={b === 1} />
                  </span>
                  <span
                    className="px-3 py-4"
                    style={{ background: "rgba(9,46,44,0.04)" }}
                  >
                    <Mark on={c === 1} />
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Onboarding, safety, partners, trust                                 */
/* ------------------------------------------------------------------ */

/**
 * Free-tier owner signup CTA.
 *
 * The page's existing pop-up signs up an OPERATOR — company name, units
 * managed, a management business. A WG landlord with one or two flats is a
 * different person with a different first step, and until now the page had
 * nothing for them: the free account exists at /owners/signup and nothing on
 * the site pointed at it.
 *
 * Deliberately a link, not another modal. The destination is a full page with
 * its own validation and an email confirmation; opening that in a dialog on
 * top of a marketing page gives it less room and a worse back button.
 */
export function LandlordFreeTier({ de }: Lang) {
  // Only shown when owner self-signup is actually enabled — with it off the
  // destination says "Noch nicht verfügbar", and a public marketing page
  // advertising a free account that then refuses you is worse than no CTA at
  // all. Renders nothing until the answer is a definite yes, so there is no
  // flash of a button that disappears.
  const signupOpen = useOwnerSelfSignupEnabled();
  if (signupOpen !== true) return null;

  return (
    <section
      className="px-5 py-20 sm:px-8 lg:px-14"
      style={{ background: SAND }}
      id="konto"
    >
      <Reveal className="mx-auto max-w-[1328px]">
        <div
          className="flex flex-col gap-8 rounded-[28px] px-7 py-10 sm:px-12 sm:py-12 lg:flex-row lg:items-center lg:justify-between"
          style={{ background: "#FFFFFF", border: `1px solid ${LINE}` }}
        >
          <div className="flex max-w-[620px] flex-col gap-3">
            <span
              className="text-[12px] font-semibold uppercase tracking-[0.14em]"
              style={{ color: MUTED }}
            >
              {de ? "Kostenloses Konto" : "Free account"}
            </span>
            <h2 className={H2} style={H2_STYLE}>
              {de
                ? "Erst ansehen, dann entscheiden"
                : "Look around first, decide after"}
            </h2>
            <p className="text-[16px] leading-[1.6]" style={{ color: MUTED }}>
              {de
                ? "Konto anlegen, Ihre Immobilie eintragen und sehen, was die Verwaltung kosten würde. Der Verwaltungsvertrag kommt erst, wenn Sie ihn wollen."
                : "Create an account, add your property and see what management would cost. The contract only follows when you want it."}
            </p>
          </div>

          <div className="flex shrink-0 flex-col items-start gap-3 lg:items-end">
            <Link
              to="/owners/signup"
              className="inline-flex h-12 items-center justify-center rounded-full px-7 text-[15px] font-semibold text-white transition-opacity hover:opacity-90"
              style={{ background: INK }}
            >
              {de ? "Kostenloses Konto erstellen" : "Create a free account"}
            </Link>
            <span className="text-[13px]" style={{ color: GREY }}>
              {de
                ? "Kostenlos — keine Zahlungsdaten nötig."
                : "Free — no payment details needed."}
            </span>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

export function LandlordOnboarding({ de }: Lang) {
  return (
    <section className="bg-white px-5 py-24 sm:px-8 lg:px-14">
      <div className="mx-auto flex max-w-[1328px] flex-col gap-10">
        <Reveal>
          <h2 className={H2} style={H2_STYLE}>
            {de ? "So starten Sie." : "This is how you start."}
          </h2>
        </Reveal>
        <div className="grid gap-7 sm:grid-cols-2 lg:grid-cols-4">
          {ONBOARDING.map((step, i) => (
            <motion.div
              key={step.name.en}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
              className="flex flex-col gap-2"
            >
              <motion.span
                variants={{
                  hidden: { scaleX: 0 },
                  visible: {
                    scaleX: 1,
                    transition: { duration: 0.7, delay: i * 0.12, ease: EASE },
                  },
                }}
                className="mb-3 block h-[1.5px] w-full origin-left"
                style={{ background: INK }}
              />
              <motion.span
                variants={{
                  hidden: { opacity: 0, y: 12 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.5, delay: i * 0.12 },
                  },
                }}
                className="text-[13px] font-bold uppercase tracking-[0.12em]"
                style={{ color: MUTED_SOFT }}
              >
                {pick(step.when, de)}
              </motion.span>
              <motion.span
                variants={{
                  hidden: { opacity: 0, y: 12 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.5, delay: i * 0.12 + 0.05 },
                  },
                }}
                className="text-[19px] font-semibold"
                style={{ letterSpacing: "-0.02em", color: INK }}
              >
                {pick(step.name, de)}
              </motion.span>
              <motion.span
                variants={{
                  hidden: { opacity: 0, y: 12 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.5, delay: i * 0.12 + 0.1 },
                  },
                }}
                className="text-[15.5px] leading-[1.6]"
                style={{ color: MUTED }}
              >
                {pick(step.body, de)}
              </motion.span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function LandlordSafety({ de }: Lang) {
  return (
    <section className="bg-white px-5 pb-24 sm:px-8 lg:px-14">
      <div className="mx-auto grid max-w-[1328px] items-start gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
        <Reveal>
          <h2 className={H2} style={H2_STYLE}>
            {de
              ? "Wenn's mal nicht rund läuft."
              : "When things do not run smoothly."}
          </h2>
        </Reveal>
        <div className="flex flex-col">
          {SAFETY.map((row, i) => (
            <Reveal key={row.name.en} delay={i}>
              <div
                className="grid gap-2 border-t py-7 sm:grid-cols-[minmax(0,260px)_minmax(0,1fr)] sm:gap-10"
                style={{ borderColor: LINE_STRONG }}
              >
                <span
                  className="text-[17px] font-semibold"
                  style={{ letterSpacing: "-0.02em", color: INK }}
                >
                  {pick(row.name, de)}
                </span>
                <span
                  className="text-[15.5px] leading-[1.6]"
                  style={{ color: MUTED }}
                >
                  {pick(row.body, de)}
                </span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function LandlordPartners({ de }: Lang) {
  return (
    <section className="bg-white px-5 pb-24 sm:px-8 lg:px-14">
      <div className="mx-auto flex max-w-[1328px] flex-col gap-8">
        <Reveal>
          <span
            className="text-[12.5px] font-bold uppercase tracking-[0.16em]"
            style={{ color: MUTED_SOFT }}
          >
            {de ? "Läuft zusammen mit" : "Works with"}
          </span>
        </Reveal>
        {/* The pills pop in one after another, then fill with petrol on hover
            — they are the page's proof of reach, so they earn the attention. */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.055 } },
          }}
          className="flex flex-wrap gap-3"
        >
          {PARTNERS.map((name) => (
            <motion.span
              key={name}
              variants={{
                hidden: { opacity: 0, y: 14, scale: 0.9 },
                visible: {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: { type: "spring", stiffness: 420, damping: 22 },
                },
              }}
              whileHover={{
                y: -4,
                backgroundColor: INK,
                color: "#FFFFFF",
                borderColor: INK,
                transition: { duration: 0.25, ease: EASE },
              }}
              className="inline-flex h-[52px] cursor-default items-center rounded-full border bg-white px-7 text-[16px] font-semibold"
              style={{ borderColor: LINE, color: INK }}
            >
              {name}
            </motion.span>
          ))}
        </motion.div>

        <div className="mt-6 grid gap-8 sm:grid-cols-3 sm:gap-10">
          {TRUST.map((item, i) => (
            <Reveal key={item.name.en} delay={i}>
              <div
                className="flex flex-col gap-2 border-t pt-6"
                style={{ borderColor: LINE_STRONG }}
              >
                <span
                  className="text-[17px] font-semibold"
                  style={{ color: INK }}
                >
                  {pick(item.name, de)}
                </span>
                <span
                  className="text-[15.5px] leading-[1.6]"
                  style={{ color: MUTED }}
                >
                  {pick(item.body, de)}
                </span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* FAQ + closing                                                       */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/* Consultation CTA                                                    */
/* ------------------------------------------------------------------ */

export function LandlordConsult({ de, onDemo }: Lang & { onDemo: () => void }) {
  return (
    <section className="bg-white px-5 pb-24 sm:px-8 lg:px-14">
      <div className="mx-auto grid max-w-[1328px] items-center gap-10 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <div className="flex flex-col gap-5">
            <h2 className={H2} style={H2_STYLE}>
              {de ? "Kostenlos beraten lassen." : "Free consultation."}
            </h2>
            <p
              className="max-w-[460px] text-[17px] leading-[1.6]"
              style={{ color: MUTED }}
            >
              {de
                ? "Produkt live sehen oder offene Fragen klären — beides kostenlos."
                : "See the product live or get your questions answered — both free."}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-4">
              <button
                type="button"
                onClick={onDemo}
                className="inline-flex h-[54px] items-center rounded-full px-8 text-[16px] font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
                style={{ background: INK }}
              >
                {de ? "Demo buchen" : "Book a demo"}
              </button>
              <a
                href="https://wa.me/498954193354"
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex h-[54px] items-center gap-2.5 rounded-full border px-7 text-[16px] font-semibold transition-all duration-300 hover:-translate-y-0.5"
                style={{ borderColor: INK, color: INK }}
              >
                <WhatsAppIcon className="h-[18px] w-[18px]" />
                {de ? "Jetzt beraten lassen" : "Talk to us now"}
              </a>
            </div>
          </div>
        </Reveal>

        <Reveal delay={1}>
          <motion.div
            whileHover={{ y: -6 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="relative h-[280px] overflow-hidden rounded-[14px] sm:h-[360px]"
            style={{ background: IMAGE_BG }}
          >
            <SmoothImage
              src="/assets/room-muc-1.png"
              alt=""
              wrapperClassName="absolute inset-0 h-full w-full"
              className="h-full w-full object-cover transition-transform duration-[900ms] ease-out hover:scale-[1.04]"
            />
          </motion.div>
        </Reveal>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Landlord voices                                                     */
/* ------------------------------------------------------------------ */

/**
 * The draft's three staggered cards, without portraits: each is a compact
 * name card. The chevron is a real control — it grows the card to show the
 * landlord's quote above the name, so the card leads with the person and the
 * testimonial is one tap away rather than three paragraphs competing for the
 * same space.
 */
export function LandlordVoices({ de }: Lang) {
  const [open, setOpen] = useState<number | null>(null);

  if (LANDLORD_VOICES.length === 0) return null;

  // The draft staggers the three cards vertically.
  const offset = ["lg:mt-12", "", "lg:mt-24"];

  return (
    <section className="bg-white px-5 pb-24 sm:px-8 lg:px-14">
      <div className="mx-auto flex max-w-[1328px] flex-col gap-12">
        <Reveal>
          <div className="flex flex-col gap-1.5">
            <h2 className={H2} style={H2_STYLE}>
              {de
                ? "Unsere wichtigste Kennzahl: Vertrauen."
                : "Our most important metric: trust."}
            </h2>
            <span className={H2} style={{ ...H2_STYLE, color: GREY }}>
              {de
                ? "Stimmen unserer WG-Vermieter."
                : "Voices from our landlords."}
            </span>
          </div>
        </Reveal>

        <div className="grid items-start gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {LANDLORD_VOICES.map((voice, i) => {
            const isOpen = open === i;
            const panelId = `voice-quote-${i}`;
            return (
              <Reveal key={voice.name} delay={i} className={offset[i % 3]}>
                <motion.figure
                  whileHover={{ y: -6 }}
                  transition={{ duration: 0.4, ease: EASE }}
                  className="relative flex flex-col overflow-hidden rounded-[18px]"
                  style={{ background: INK_DEEP }}
                >
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        id={panelId}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.32, ease: EASE }}
                        className="overflow-hidden"
                      >
                        <div
                          className="mx-4 mt-4 flex flex-col gap-3 rounded-[14px] bg-white px-6 py-5"
                          style={{
                            boxShadow: "0 24px 48px rgba(3,18,17,0.35)",
                          }}
                        >
                          <span className="flex gap-[3px]" aria-hidden>
                            {Array.from({ length: 5 }).map((_, s) => (
                              <Star
                                key={s}
                                className="h-3.5 w-3.5"
                                style={{ color: INK }}
                                fill="currentColor"
                                strokeWidth={0}
                              />
                            ))}
                          </span>
                          <p
                            className="text-[15.5px] leading-[1.55]"
                            style={{ color: INK }}
                          >
                            {`„${pick(voice.quote, de)}“`}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <figcaption className="flex items-end justify-between gap-4 p-7">
                    <span className="flex flex-col gap-1">
                      <span className="text-[19px] font-semibold text-white">
                        {voice.name}
                      </span>
                      <span
                        className="text-[14.5px]"
                        style={{ color: "rgba(255,255,255,0.82)" }}
                      >
                        {pick(voice.role, de)}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => setOpen(isOpen ? null : i)}
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      className="grid h-10 w-10 shrink-0 place-items-center rounded-full transition-colors duration-300"
                      style={{
                        background: isOpen
                          ? "#FFFFFF"
                          : "rgba(255,255,255,0.16)",
                        color: isOpen ? INK : "#FFFFFF",
                      }}
                    >
                      <span className="sr-only">
                        {de
                          ? `Stimme von ${voice.name} ${isOpen ? "ausblenden" : "anzeigen"}`
                          : `${isOpen ? "Hide" : "Show"} ${voice.name}'s quote`}
                      </span>
                      {/* Right when closed, up when open — the draft's own
                          pair, and the up arrow reads as "put it back". */}
                      <ChevronRight
                        className="h-4 w-4 transition-transform duration-300"
                        strokeWidth={2.4}
                        style={{
                          transform: isOpen ? "rotate(-90deg)" : undefined,
                        }}
                      />
                    </button>
                  </figcaption>
                </motion.figure>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function LandlordFaq({ de }: Lang) {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <section
      className="px-5 py-24 sm:px-8 lg:px-14"
      style={{ background: SAND }}
    >
      <div className="mx-auto grid max-w-[1328px] items-start gap-10 lg:grid-cols-[1fr_1.15fr] lg:gap-14">
        <Reveal>
          <h2 className={H2} style={H2_STYLE}>
            {de ? "Häufige Fragen." : "Frequently asked."}
          </h2>
        </Reveal>
        <div className="flex flex-col">
          {OWNER_FAQ.map((item, i) => {
            const on = open === i;
            return (
              <Reveal key={item.q.en} delay={Math.min(i, 4)}>
                <div className="border-t" style={{ borderColor: LINE_STRONG }}>
                  <button
                    type="button"
                    onClick={() => setOpen(on ? null : i)}
                    aria-expanded={on}
                    className="flex w-full items-center justify-between gap-5 py-6 text-left"
                  >
                    <span
                      className="text-[17px] font-semibold sm:text-[19px]"
                      style={{ letterSpacing: "-0.02em", color: INK }}
                    >
                      {pick(item.q, de)}
                    </span>
                    <ChevronDown
                      className={`h-5 w-5 shrink-0 transition-transform duration-300 ${
                        on ? "rotate-180" : ""
                      }`}
                      style={{ color: INK }}
                      strokeWidth={2.2}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {on && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: EASE }}
                        className="overflow-hidden"
                      >
                        <p
                          className="max-w-[68ch] pb-6 text-[15.5px] leading-[1.65]"
                          style={{ color: MUTED }}
                        >
                          {pick(item.a, de)}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function LandlordClosing({ de, onDemo }: Lang & { onDemo: () => void }) {
  return (
    <section
      className="px-5 pt-24 sm:px-8 lg:px-14"
      style={{ background: INK_DEEP }}
    >
      <Reveal className="mx-auto max-w-[1328px]">
        <div className="flex flex-col gap-5 pb-14">
          <h2
            className="max-w-[900px] font-display text-[2rem] font-semibold leading-[1.06] text-white sm:text-[2.5rem]"
            style={{ letterSpacing: "-0.04em" }}
          >
            {de
              ? "Lassen Sie uns über Ihre WG sprechen."
              : "Let's talk about your shared flat."}
          </h2>
          <p
            className="max-w-[460px] text-[16px] leading-[1.6]"
            style={{ color: "rgba(247,243,236,0.72)" }}
          >
            {de
              ? "15 Minuten, unverbindlich. Danach wissen Sie, was coliviq für Ihr Objekt übernimmt."
              : "15 minutes, no obligation. After that you know what coliviq would take on for your property."}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={onDemo}
              className="inline-flex h-[54px] items-center rounded-full bg-white px-8 text-[16px] font-semibold transition-transform duration-300 hover:-translate-y-0.5"
              style={{ color: INK_DEEP }}
            >
              {de ? "Gespräch oder Demo buchen" : "Book a call or a demo"}
            </button>
            <Link
              to="/contact"
              className="inline-flex h-[54px] items-center rounded-full border-[1.5px] px-7 text-[16px] font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-white/10"
              style={{ borderColor: "rgba(255,255,255,0.3)" }}
            >
              {de ? "Schreib uns" : "Write to us"}
            </Link>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
